import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetVoucherStatementQuery } from '../../../api/voucher/voucherApiSlice';
import { VoucherActor } from '../../../api/voucher/voucherApiTypes';
import { makeRentalBookingDetailsUrl } from '../../../routes';

interface VoucherStatementPanelProps {
    voucherId: string;
    currency: string | null;
    naLabel: string;
    formatMoney: (value: number | null, currency: string | null) => string;
    formatDate: (iso: string | null) => string;
}

const headerCell = 'text-left px-3 py-2 text-foreground/60 font-medium';
const cell = 'px-3 py-2 text-foreground/80';

/**
 * A prepaid voucher's full statement (#3261): every redemption, refund and
 * chargeback line, and where the voucher came from — source, who created it,
 * why. Only rendered for `view:voucher-history`; the endpoint answers 403
 * without it. Refetched on every open: refunds arrive from Stripe.
 */
const VoucherStatementPanel: FC<VoucherStatementPanelProps> = ({
    voucherId,
    currency,
    naLabel,
    formatMoney,
    formatDate,
}) => {
    const { t } = useTranslation();
    const { data, isLoading, isError } = useGetVoucherStatementQuery(voucherId, {
        refetchOnMountOrArgChange: true,
    });

    if (isLoading) {
        return <p className={'text-sm text-foreground/60 px-4 py-3'}>{t('common.loading')}</p>;
    }
    if (isError || !data) {
        return <p className={'text-sm text-foreground/60 px-4 py-3'}>{t('vouchers_codes.statement.error')}</p>;
    }

    const person = (actor: VoucherActor | null) => (actor ? actor.name ?? actor.subject : naLabel);
    // A promo or goodwill code has only redemptions: it keeps the titles its
    // redemption panel had (#3332 D4).
    const isPrepaid = data.codeType === 'PREPAID_VOUCHER';
    const sourceLabel = data.source
        ? t(`vouchers_codes.prepaid_list.source.${data.source}`, { defaultValue: data.source })
        : naLabel;

    return (
        <div className={'px-4 py-3 space-y-3'}>
            <dl className={'grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm'}>
                <div>
                    <dt className={'text-foreground/60'}>{t('vouchers_codes.statement.source')}</dt>
                    <dd className={'text-foreground/80'}>{sourceLabel}</dd>
                </div>
                <div>
                    <dt className={'text-foreground/60'}>{t('vouchers_codes.statement.created_by')}</dt>
                    <dd className={'text-foreground/80'}>{person(data.createdBy)}</dd>
                </div>
                <div>
                    <dt className={'text-foreground/60'}>{t('vouchers_codes.statement.reason')}</dt>
                    <dd className={'text-foreground/80'}>{data.creationReason ?? naLabel}</dd>
                </div>
            </dl>
            <p className={'text-sm font-medium text-foreground/80'}>
                {t(isPrepaid ? 'vouchers_codes.statement.title' : 'vouchers_codes.redemptions.title')}
            </p>
            {data.lines.length === 0 ? (
                <p className={'text-sm text-foreground/60'}>
                    {t(isPrepaid ? 'vouchers_codes.statement.empty' : 'vouchers_codes.redemptions.empty')}
                </p>
            ) : (
                <table className={'w-full text-sm'}>
                    <thead>
                        <tr className={'border-b border-foreground/10'}>
                            <th className={headerCell}>{t('vouchers_codes.statement.date')}</th>
                            <th className={headerCell}>{t('vouchers_codes.statement.kind')}</th>
                            <th className={headerCell}>{t('vouchers_codes.statement.amount')}</th>
                            <th className={headerCell}>{t('vouchers_codes.statement.booking')}</th>
                            <th className={headerCell}>{t('vouchers_codes.statement.note')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.lines.map((l) => (
                            <tr key={l.id} className={'border-b border-foreground/5 last:border-b-0'}>
                                <td className={cell}>{formatDate(l.at)}</td>
                                <td className={cell}>
                                    {l.kind === 'chargeback' && l.disputeEvent
                                        ? t(`vouchers_codes.statement.kinds.${l.disputeEvent}`)
                                        : t(`vouchers_codes.statement.kinds.${l.kind}`)}
                                </td>
                                <td className={cell}>{l.amount > 0 ? formatMoney(l.amount, currency) : naLabel}</td>
                                <td className={cell}>
                                    {l.orderId && l.bookingNumber ? (
                                        <Link to={makeRentalBookingDetailsUrl(l.orderId)} className={'text-primary hover:underline'}>
                                            {l.bookingNumber}
                                        </Link>
                                    ) : (
                                        l.bookingNumber ?? naLabel
                                    )}
                                </td>
                                <td className={cell}>
                                    {[l.reason, l.actor ? person(l.actor) : null].filter(Boolean).join(' · ') || naLabel}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default VoucherStatementPanel;
